import React from "react";
import { LegalPage, LegalSection } from "./LegalPage";

export const Terms = () => (
  <LegalPage
    eyebrow="Legal · Website Use"
    title="Terms & Conditions"
    lastUpdated="February 2026"
  >
    <LegalSection heading="About FIND LET">
      <p>
        FIND LET is a London rental matching, property introduction and relocation/concierge service. In these terms, "FIND LET", "we", "us" and "our" refer to the trading name FIND LET. "You" and "your" refer to the person using the website or service.
      </p>
      <p>
        FIND LET does not own properties displayed or submitted on the platform unless expressly stated. Property information may be provided by third parties, including landlords, property partners and letting agents.
      </p>
      <p className="text-xs italic text-[#7096D1]">
        {/* Post-incorporation TODO: replace this paragraph with the registered legal operator name, company number and registered office once available. */}
        Full operator details will be published here once FIND LET has completed incorporation.
      </p>
    </LegalSection>

    <LegalSection heading="Acceptance of these terms">
      <p>
        By using the FIND LET website, submitting a renter brief, listing a property, submitting a property partner enquiry, requesting a FIND LET Move service, sending a contact message or registering an account, you agree to be bound by these Terms &amp; Conditions and our{" "}
        <a href="/privacy" className="editorial-link">Privacy Policy</a>.
      </p>
      <p>
        If you do not agree with these terms, please do not use the service.
      </p>
    </LegalSection>

    <LegalSection heading="Eligibility">
      <p>
        You must be at least 18 years old and legally able to enter into a binding agreement to use the service. By using FIND LET, you confirm that you meet these requirements.
      </p>
    </LegalSection>

    <LegalSection heading="Your account">
      <p>
        You are responsible for the accuracy of any information you submit and for keeping your account credentials confidential. You are responsible for any activity that takes place under your account. Please contact us at{" "}
        <a href="mailto:concierge@findlet.uk" className="editorial-link">concierge@findlet.uk</a>{" "}
        if you believe your account has been accessed without your permission.
      </p>
    </LegalSection>

    <LegalSection heading="Renter briefs">
      <p>
        When you submit a renter brief, you confirm that the information you provide is accurate and that you have the authority to share it with FIND LET for the purpose of receiving property introductions.
      </p>
      <p>
        We will consider your brief and, where suitable options exist, may share relevant details with landlords or property partners to facilitate a potential match. Submitting a brief does not guarantee a property match, viewing or tenancy.
      </p>
    </LegalSection>

    <LegalSection heading="Landlord and property submissions">
      <p>
        When you submit a property, you confirm that:
      </p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>You are the owner, an authorised representative or otherwise entitled to advertise the property.</li>
        <li>The details you provide are accurate, complete and not misleading.</li>
        <li>You will keep the availability and status of the property up to date.</li>
        <li>You have the necessary permissions and rights to any photography, floor plans or documents you submit.</li>
      </ul>
      <p>
        FIND LET may verify submitted information and may decline, edit or remove listings that are inaccurate, misleading, unavailable or otherwise unsuitable for the platform.
      </p>
    </LegalSection>

    <LegalSection heading="Property availability and introductions">
      <p>
        Property availability, rent, deposit and other property details are indicative and subject to change and confirmation by the landlord or property partner. FIND LET does not guarantee that a particular property is or will remain available.
      </p>
      <p>
        FIND LET provides introductions between renters and landlords or property partners. Any tenancy, contract, viewing or agreement is a matter between the parties involved. FIND LET is not a party to any such tenancy or contract.
      </p>
    </LegalSection>

    <LegalSection heading="Third-party providers">
      <p>
        Where you request a FIND LET Move service or related concierge service, we may introduce you to third-party providers such as removals, cleaning, storage or utilities partners. Any service delivered by a third-party provider is subject to that provider's own terms and conditions and is the responsibility of that provider.
      </p>
    </LegalSection>

    <LegalSection heading="Fees">
      <p>
        Where any fees are applicable, they will be separately and clearly disclosed to you before you incur them. Unless we have separately agreed a fee in writing, the FIND LET platform is free to use for renters.
      </p>
    </LegalSection>

    <LegalSection heading="Acceptable use">
      <p>You agree not to:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Provide false, misleading or fraudulent information.</li>
        <li>Impersonate another person or misrepresent your affiliation with any person or organisation.</li>
        <li>Use the service to send unsolicited communications, spam or unlawful content.</li>
        <li>Attempt to gain unauthorised access to the platform or any related systems.</li>
        <li>Interfere with the operation of the platform or the experience of other users.</li>
        <li>Scrape, copy or otherwise extract data from the platform without our permission.</li>
        <li>Use the service for any unlawful purpose or in breach of any applicable law.</li>
      </ul>
    </LegalSection>

    <LegalSection heading="Intellectual property">
      <p>
        The FIND LET name, branding, website content, design, layout and code are owned by FIND LET or its licensors and are protected by applicable intellectual property laws. You may use the website only for the purposes described in these terms. You may not copy, modify, distribute or otherwise use FIND LET content without our written permission.
      </p>
      <p>
        You retain ownership of any content or information you submit to us. By submitting it, you grant FIND LET a licence to use, process and share it as necessary to provide the service and administer the platform.
      </p>
    </LegalSection>

    <LegalSection heading="Service availability">
      <p>
        We aim to keep the platform available but do not guarantee uninterrupted access. We may modify, suspend or discontinue parts of the service at any time. We may also suspend or terminate your access if you breach these terms or misuse the service.
      </p>
    </LegalSection>

    <LegalSection heading="Nature of the service">
      <p>
        FIND LET provides a rental matching, introduction and concierge service only. FIND LET does not provide legal, financial or tenancy advice. Any decisions you make about a property, tenancy or move are your own. We recommend seeking independent professional advice where appropriate.
      </p>
    </LegalSection>

    <LegalSection heading="Limitation of liability">
      <p>
        To the extent permitted by law, FIND LET is not liable for any indirect, incidental or consequential loss arising from your use of the platform, from any introduction facilitated through the platform, or from the acts, omissions or services of any landlord, property partner or third-party provider.
      </p>
      <p>
        Nothing in these terms limits any liability that cannot be excluded under applicable law.
      </p>
    </LegalSection>

    <LegalSection heading="Complaints">
      <p>
        If something has gone wrong, please contact us at{" "}
        <a href="mailto:concierge@findlet.uk" className="editorial-link">concierge@findlet.uk</a>{" "}
        so we can look into it and try to put it right.
      </p>
    </LegalSection>

    <LegalSection heading="Changes to these terms">
      <p>
        We may update these terms from time to time. The "Last updated" date at the top of the page reflects the most recent version. Continued use of the service after changes take effect means you accept the updated terms.
      </p>
    </LegalSection>

    <LegalSection heading="Governing law">
      <p>
        These terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction over any dispute arising from these terms or your use of the service.
      </p>
    </LegalSection>
  </LegalPage>
);
